import * as Sentry from "@sentry/react";
import { useAuthV2 } from "@/lib/authv2/store";

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
  });
}

/** Khu tải ảnh có ghi nhật ký lỗi (lượt kiểm 24/10/2026, L8). */
export type UploadErrorArea = "album-upload" | "order-photos" | "compose-read" | "compose-save";

/** Mỗi khu × loại lỗi ghi tối đa chừng này lần mỗi lần mở trang — mất mạng cả buổi không thành nghìn dòng. */
const UPLOAD_REPORTS_PER_KIND = 5;
const uploadReportCounts = new Map<string, number>();

/**
 * Loại lỗi đọc từ hình dạng lỗi (tên lớp, mã kho, mã máy chủ, đã gửi hết thân
 * chưa) — KHÔNG từ câu báo: câu báo mang tên tệp, mà tên tệp có thể là tên khách.
 */
function uploadErrorKind(error: unknown): string {
  if (!(error instanceof Error)) return "unknown";
  const facts = error as Error & { status?: unknown; code?: unknown; bodySent?: unknown; kind?: unknown };
  const parts = [error.name];
  if (typeof facts.kind === "string") parts.push(facts.kind);
  if (typeof facts.status === "number") parts.push(String(facts.status));
  if (typeof facts.code === "string" && facts.code) parts.push(facts.code);
  if (typeof facts.bodySent === "boolean") parts.push(facts.bodySent ? "body-sent" : "body-not-sent");
  if (error.cause instanceof TypeError) parts.push("network");
  return parts.join(":");
}

/**
 * Ghi một lỗi tải ảnh lên / xuống vào nhật ký lỗi (Sentry), kèm cơ sở và tình
 * trạng mạng — để đo được cơ sở nào, giờ nào, lỗi gì (trước 25/10/2026 lỗi tải
 * ảnh không để lại dấu ở đâu). Không ghi tên tệp, link, nội dung câu báo.
 * Tạm dừng (AbortError) không phải lỗi. Bản dựng thiếu VITE_SENTRY_DSN thì
 * Sentry chưa bật và lệnh này không làm gì.
 */
export function reportUploadError(area: UploadErrorArea, error: unknown, extra: Record<string, number> = {}): void {
  if ((error as { name?: unknown } | null)?.name === "AbortError") return;
  const kind = uploadErrorKind(error);
  const key = `${area}|${kind}`;
  const count = uploadReportCounts.get(key) ?? 0;
  if (count >= UPLOAD_REPORTS_PER_KIND) return;
  uploadReportCounts.set(key, count + 1);
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection;
  Sentry.captureMessage(`Tải ảnh lỗi: ${area}`, {
    level: "warning",
    fingerprint: ["upload-error", area, kind],
    tags: {
      upload_area: area,
      upload_error: kind,
      branch: useAuthV2.getState().branchScope?.primaryBranch || "unknown",
      online: String(navigator.onLine),
      network: connection?.effectiveType ?? "unknown",
    },
    extra,
  });
}

/** Khu tải ảnh LÊN (compose-read là tải xuống, không đo chậm). */
export type SlowUploadArea = Exclude<UploadErrorArea, "compose-read">;

export type UploadTiming = {
  /** Cả chặng của một tấm, gồm cả lúc chờ (hỏi máy chủ, mạng đứt rồi gửi lại, bị bảo gọi chậm lại). */
  totalMs: number;
  /** Riêng lúc gửi ảnh thành công. Nhỏ hơn hẳn `totalMs` nghĩa là chậm vì chờ, không vì đường truyền. */
  sendMs?: number;
  bytes: number;
};

/** Một tấm từ chừng này trở lên thì ghi nhật ký tải chậm (CEO chốt 1a, 02/11/2026). */
export const SLOW_UPLOAD_MS = 30_000;

function slowBucket(totalMs: number): string {
  if (totalMs < 60_000) return "30-60s";
  return totalMs < 120_000 ? "1-2m" : "2m+";
}

/**
 * Ghi một tấm tải LÂU mà vẫn xong vào nhật ký (Sentry), kèm cơ sở và tình trạng
 * mạng — lỗi tải đã có `reportUploadError`, còn tải chậm trước 02/11/2026 không
 * để lại dấu gì. Không ghi tên tệp, link. Bản dựng thiếu VITE_SENTRY_DSN thì
 * Sentry chưa bật và lệnh này không làm gì.
 */
export function reportSlowUpload(area: SlowUploadArea, timing: UploadTiming): void {
  if (timing.totalMs < SLOW_UPLOAD_MS) return;
  const bucket = slowBucket(timing.totalMs);
  const key = `slow|${area}|${bucket}`;
  const count = uploadReportCounts.get(key) ?? 0;
  if (count >= UPLOAD_REPORTS_PER_KIND) return;
  uploadReportCounts.set(key, count + 1);
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection;
  Sentry.captureMessage(`Tải ảnh chậm: ${area}`, {
    level: "warning",
    fingerprint: ["upload-slow", area],
    tags: {
      upload_area: area,
      upload_slow: bucket,
      branch: useAuthV2.getState().branchScope?.primaryBranch || "unknown",
      online: String(navigator.onLine),
      network: connection?.effectiveType ?? "unknown",
    },
    extra: {
      seconds: Math.round(timing.totalMs / 1000),
      ...(timing.sendMs === undefined ? {} : { sendSeconds: Math.round(timing.sendMs / 1000) }),
      bytes: timing.bytes,
    },
  });
}

/** Đo một lượt lưu một tấm từ đầu tới lúc xong; hỏng thì ném nguyên lỗi, không tính là chậm. */
export async function timedUpload<T>(area: SlowUploadArea, bytes: number, upload: () => Promise<T>): Promise<T> {
  const startedAt = performance.now();
  const result = await upload();
  reportSlowUpload(area, { totalMs: performance.now() - startedAt, bytes });
  return result;
}

/** For tests: forget how many reports each kind already sent. */
export function resetUploadReportsForTests(): void {
  uploadReportCounts.clear();
}
